import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Body5, Body7, Container, TextField } from '@ui';
import { FlatButton } from '@ui/buttons';
import { DragDrop } from '@ui/DragAndDropUploader';
import { ROUTES } from '@constants/routes';
import { UPLOAD_EXTENSIONS, fileExtensionOf, uploadModel } from '@services/fmdc';
import { FmdcTenantContext } from './FmdcTenantContext';
import { NOTICE_TONES, NoticeText } from './NoticeText';
import { isIssuerNameValid, isIssuerValid } from './RerunConfirm';
import { fmdcPaths } from './fmdcUrl';
import { Card, FileChip, FileChipRemoveButton, LinkButton } from './styles';

// Upload an analyst's Excel model for data capture. One file, a name, an optional issuer; the
// response says whether a run started or the model was already uploaded (which is not an error).

const ISSUER_HINT = 'Decisions carry over to later uploads of this issuer only when an issuer id is set.';
const ISSUER_INVALID_HINT = '1 to 64 characters: letters, digits, space, . _ - or /';

const Fields = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const fullFilename = (picked) =>
  picked?.file?.name ? picked.file.name : `${picked?.name || ''}.${picked?.type || ''}`;

export const unsupportedExtensionMessage = (ext) =>
  `.${ext || '?'} files are not supported here. Upload a ${UPLOAD_EXTENSIONS.map((e) => `.${e}`).join(', ')} model.`;

export const UploadPanel = ({ onUploaded }) => {
  const navigate = useNavigate();
  const { reload: reloadTenant } = React.useContext(FmdcTenantContext);

  const [picked, setPicked] = React.useState(null);
  const [fileError, setFileError] = React.useState(null);
  const [name, setName] = React.useState('');
  const [issuerId, setIssuerId] = React.useState('');
  const [issuerName, setIssuerName] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState(null);

  const handleAddFiles = (files) => {
    if (!files || files.length === 0) return;
    // One model per run: the dropzone is always multiple, so the last file dropped wins.
    const last = files[files.length - 1];
    const ext = fileExtensionOf(last.file);
    if (!UPLOAD_EXTENSIONS.includes(ext)) {
      // The dropzone's xls preset also admits .xlsb, which the upload route has no field for.
      setFileError(unsupportedExtensionMessage(ext));
      setPicked(null);
      return;
    }
    setFileError(null);
    setNotice(null);
    setPicked(last);
    setName(last.name || '');
  };

  const handleRemoveFile = () => {
    setPicked(null);
    setName('');
  };

  const issuerIdTrimmed = issuerId.trim();
  const issuerNameTrimmed = issuerName.trim();
  const issuerInvalid = !isIssuerValid(issuerIdTrimmed);
  const issuerNameInvalid = !isIssuerNameValid(issuerNameTrimmed);
  const canSubmit = !!picked && name.trim() !== '' && !issuerInvalid && !issuerNameInvalid && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setNotice(null);
    const result = await uploadModel({
      file: picked.file,
      name: name.trim(),
      issuerId: issuerIdTrimmed || undefined,
      issuerName: issuerNameTrimmed || undefined,
    });
    setBusy(false);

    if (result.error) {
      setNotice({ tone: NOTICE_TONES.ERROR, text: result.error });
      // "Financial model data capture is not enabled for this account": the tenant changed under
      // us, and the page root's gate is what should say so.
      if (/not enabled/i.test(result.error)) reloadTenant();
      return;
    }
    if (result.duplicate) {
      setNotice({
        tone: NOTICE_TONES.NEUTRAL,
        text: `This model was already uploaded as run #${result.runId}.`,
        runId: result.runId,
      });
      return;
    }
    setNotice({ tone: NOTICE_TONES.POSITIVE, text: `Processing. Run #${result.runId} started.` });
    setPicked(null);
    setName('');
    if (onUploaded) onUploaded(result.runId);
  };

  return (
    <Card>
      {/* No heading: the disclosure that opens this card is titled "Upload a model" already. */}
      <Body7 isLowContrast>
        An analyst's Excel model (.xlsx, .xlsm or .xls). Every value is extracted into the client's template; a run
        takes a few minutes.
      </Body7>

      <Container top={16}>
        {picked ? (
          <FileChip>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            <Body5 truncate title={fullFilename(picked)}>
              {fullFilename(picked)}
            </Body5>
            <FileChipRemoveButton onClick={handleRemoveFile} aria-label="Remove file">
              <span aria-hidden="true">×</span>
            </FileChipRemoveButton>
          </FileChip>
        ) : (
          <DragDrop onAddFiles={handleAddFiles} xls includePdf={false} />
        )}
      </Container>

      {fileError && (
        <Container top={8}>
          <NoticeText tone={NOTICE_TONES.ERROR} small>
            {fileError}
          </NoticeText>
        </Container>
      )}

      <Container top={16}>
        <Fields>
          <TextField
            label="Name"
            value={name}
            onChange={setName}
            placeholder="Document name"
            hint={!picked ? 'Filled in from the file name.' : undefined}
          />
          <TextField
            label="Issuer id"
            value={issuerId}
            onChange={setIssuerId}
            invalid={issuerInvalid}
            hint={issuerInvalid ? ISSUER_INVALID_HINT : ISSUER_HINT}
          />
          <TextField
            label="Issuer name"
            value={issuerName}
            onChange={setIssuerName}
            placeholder="Acme Corp"
            invalid={issuerNameInvalid}
            hint={issuerNameInvalid ? 'Up to 64 characters' : 'Optional'}
          />
        </Fields>
      </Container>

      <Container top={16} row verticallyCenter gap={16} flexWrap>
        <FlatButton onClick={handleSubmit} disabled={!canSubmit}>
          {busy ? 'Uploading…' : 'Upload and extract'}
        </FlatButton>
        {!picked && !busy && <Body7 isLowContrast>Drop a model above to enable the upload.</Body7>}
      </Container>

      {notice && (
        <Container top={12} row verticallyCenter gap={12} flexWrap>
          <NoticeText tone={notice.tone}>{notice.text}</NoticeText>
          {notice.runId && (
            <LinkButton onClick={() => navigate(fmdcPaths.run(ROUTES.fmdc, notice.runId))}>
              Open run #{notice.runId}
            </LinkButton>
          )}
        </Container>
      )}
    </Card>
  );
};
